import { AnalyticsEventTopic } from '~/modules/analytics/analyticsHelper';
import { getQueryStringValue } from '~/modules/analytics/gtmEventTypes/gtmEventTypeHelper';
import { GA4ContentClickEvent, GTMBaseEvent, GTMVersion, GtmEventType } from './gtmEventType';

interface EventGA4CustomProperties {
    click_url?: string;
    click_text: string;
    element_title: string;
    content_category?: string;
    internal_driver?: string;
}

type GTMButtonEvent = {
    button: {
        value?: string;
        type?: string;
        callToAction: {
            label: string;
            name: string;
        };
    };
    title?: string;
    url?: string;
} & GTMBaseEvent;

type GTMTopCategoriesEvent = {
    tag: {
        callToAction: {
            label: string;
            name: string;
            url: string;
        };
    };
    topCategories: { title: string };
} & GTMBaseEvent;

type GTMBannerEvent = {
    banner: {
        title?: string;
        callToAction: {
            label: string;
            url?: string;
        };
    };
} & GTMBaseEvent;

export type GTMClickEvent = GTMButtonEvent | GTMBannerEvent | GTMTopCategoriesEvent;

export class GtmButtonClickType extends GtmEventType {
    canHandle(event: GTMBaseEvent): boolean {
        return (
            event.topic === AnalyticsEventTopic.ANALYTICS_CLICK &&
            ((event.elementType === 'button' && !event.oafId && !event.keyword && !event.currentListingId) ||
                (event.elementType === 'tag' && !event.listing))
        );
    }

    getBody(event: GTMBaseEvent): GA4ContentClickEvent {
        return this.getGa4Body(event as GTMClickEvent);
    }

    isButton(event: GTMClickEvent): event is GTMButtonEvent {
        return 'button' in event;
    }

    isTopCategories(event: GTMClickEvent): event is GTMTopCategoriesEvent {
        return 'topCategories' in event;
    }

    isBanner(event: GTMClickEvent): event is GTMBannerEvent {
        return 'banner' in event;
    }
    getContextByButton(buttonName: string) {
        if (buttonName && buttonName.indexOf('header') >= 0) {
            return 'header';
        } else if (buttonName && buttonName.indexOf('toast') >= 0) {
            return 'toast';
        }
        return '';
    }

    getGtmVersion(event: GTMBaseEvent): GTMVersion {
        return event.gtm || 'ga';
    }

    parseGA4BucketClickEvent(event: GTMButtonEvent): GA4ContentClickEvent {
        return {
            event: 'custEv_contentClick',
            click_text: event.button.value || event.button.callToAction?.name,
            element_title: event.title || 'filter category',
            element_type: event.button.type || event.elementType,
        };
    }

    parseGA4ButtonEvent(event: GTMButtonEvent): EventGA4CustomProperties {
        const {
            callToAction: { label, name },
        } = event.button;
        return {
            click_text: label,
            element_title: label,
            content_category: name,
        };
    }

    parseGA4BannerEvent(event: GTMBannerEvent): EventGA4CustomProperties {
        const {
            callToAction: { label, url },
            title,
        } = event.banner;
        return {
            click_text: label,
            element_title: title || label,
            click_url: url,
            internal_driver: url ? getQueryStringValue(url, 'd') : '',
        };
    }

    parseGA4TopCategoriesEvent(event: GTMTopCategoriesEvent) {
        const {
            topCategories: { title },
            tag: {
                callToAction: { name, url },
            },
        } = event;
        return {
            click_text: name,
            element_title: title,
            click_url: url,
            content_category: 'Top Categories',
            internal_driver: getQueryStringValue(url, 'd'),
        };
    }

    getGa4Body(event: GTMClickEvent): GA4ContentClickEvent {
        const commonEventProps = {
            event: 'custEv_contentClick',
            element_type: event.elementType,
        };

        if (this.getGtmVersion(event) === 'ga4') {
            return this.parseGA4BucketClickEvent(event as GTMButtonEvent);
        }
        if (this.isButton(event)) {
            return { ...commonEventProps, ...this.parseGA4ButtonEvent(event) };
        }
        if (this.isTopCategories(event)) {
            return { ...commonEventProps, ...this.parseGA4TopCategoriesEvent(event) };
        }
        if (this.isBanner(event)) {
            return { ...commonEventProps, ...this.parseGA4BannerEvent(event) };
        }
        return commonEventProps;
    }
}
